import { alpha, useTheme } from "@mui/material";
import { FeatureCollection, MultiPolygon } from "geojson";
import { Feature, Map } from "ol";
import GeoJSON from "ol/format/GeoJSON";
import Geometry from "ol/geom/Geometry";
import VectorLayer from "ol/layer/Vector";
import { Fill, Stroke } from "ol/style";
import Style, { Options } from "ol/style/Style";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  projectShapePlanToMapItem,
  projectShapeProgressToMapItem,
} from "~/pages/MapPage/functions/dataConverters";
import { findLayerByTitle } from "~/pages/MapPage/functions/findLayerByTitle";
import { zoomOnFeature } from "~/pages/MapPage/functions/zoomOnFeature";
import useCreateMap from "~/pages/MapPage/hooks/useCreateMap";
import { LayerTitle } from "~/pages/MapPage/types/layers";
import { createCanvasPattern } from "~/utils/createCanvasPattern";
import { useCellNamePolygon } from "./useCellNamePolygon";
import {
  GeojsonAttributes,
  useProjectShapeFile,
  UseShapeFileTypes,
} from "./useProjectShapeFile";

export const planningColor = "#ed6c02";

export enum ColorOptionEnum {
  Vessel = 1,
  SubArea = 2,
}

export type DrawGeoJsonTypes = {
  progressGeoJson:
    | FeatureCollection<MultiPolygon, GeojsonAttributes>
    | undefined;
  planGeoJson?: FeatureCollection<MultiPolygon, GeojsonAttributes> | undefined;
};

type DrawProjectMapOptions = {
  vessels: string[];
  showPlanning: boolean;
  areaOptions: string[];
  planAreas: UseShapeFileTypes["planAreas"];
  colorBy: keyof GeojsonAttributes;
};

type UseProjectMapProps = {
  existingMap?: Map;
  wprView?: boolean;
};

type SharedMapProps = {
  sliderValue: number | number[];
  id: number | undefined;
  options?: DrawProjectMapOptions | null;
  progressOpacity?: number;
  planningOpacity?: number;
};

export const useProjectMap = ({
  existingMap,
  ...rest
}: UseProjectMapProps & SharedMapProps) => {
  const { createMap } = useCreateMap();
  const [mapState, setMapState] = React.useState<Map>();

  React.useEffect(() => {
    if (mapState !== undefined) return;
    const map = createMap({
      target: "mapContainer",
      existingMap,
      includedLayers: [
        LayerTitle.OSM,
        LayerTitle.Projects,
        LayerTitle.ProjectsPlanning,
        LayerTitle.ProjectPoiItems,
      ],
    });
    if (map) setMapState(map);
  }, [createMap, existingMap, mapState]);

  useProjectMapDraw({
    map: mapState,
    ...rest,
  });
  return { map: mapState };
};

type UseDrawProjectMapProps = {
  map?: Map;
  styleOptions?: Options;
  wprView?: boolean;
};

export function useProjectMapDraw({
  map,
  id,
  options,
  progressOpacity = 1,
  planningOpacity,
  wprView,
  ...rest
}: UseDrawProjectMapProps & SharedMapProps) {
  const { sliderValue } = rest;
  const {
    progressJson: progressGeoJson,
    planJson: planGeoJson,
    progressLoading,
    mapItemColors,
  } = useProjectShapeFile({ id });
  const [hasZoomed, setHasZoomed] = React.useState(false);
  const { t } = useTranslation();
  const [progressLayer, setProgressLayer] = React.useState<VectorLayer>();
  const [planningLayer, setplanningLayer] = React.useState<VectorLayer>();
  const [planningFeatureAdded, setPlanningFeatureAdded] = React.useState(false);
  const [progressFeatureAdded, setProgressFeatureAdded] = React.useState(false);
  const [filteredPlanFeatures, setFilteredPlanFeatures] = React.useState<
    Feature<Geometry>[]
  >([]);

  useCellNamePolygon({
    map,
    features: filteredPlanFeatures,
    enabled: wprView && options?.showPlanning,
  });

  const theme = useTheme();
  React.useEffect(() => {
    const pLayer = findLayerByTitle(map, LayerTitle.Projects);
    const planningL = findLayerByTitle(
      map,
      LayerTitle.ProjectsPlanning,
    ) as VectorLayer;

    if (pLayer) {
      const vLayer = pLayer as VectorLayer;
      setProgressLayer(vLayer);
    }
    if (planningL) {
      planningL.setStyle(
        new Style({
          stroke: new Stroke({
            width: 2,
          }),
          fill: new Fill({ color: alpha(planningColor, 0.2) }),
        }),
      );
      setplanningLayer(planningL);
    }
  }, [map]);

  // set style to planning layer
  React.useEffect(() => {
    if (planningOpacity === undefined) return;
    planningLayer?.setStyle(
      new Style({
        stroke: new Stroke({
          width: 2,
        }),
        fill: new Fill({ color: alpha(planningColor, planningOpacity) }),
      }),
    );
  }, [planningLayer, planningOpacity]);

  // zoom to layers
  React.useEffect(() => {
    if (!map || hasZoomed || progressLoading) return;

    const source = planningLayer?.getSource();
    const sourceExtend = source?.getExtent();
    if (sourceExtend && !sourceExtend.includes(Infinity)) {
      map?.getView().fit(sourceExtend);
      setHasZoomed(true);
    } else if (progressFeatureAdded) {
      const progressDidZoom = zoomOnFeature(
        map,
        `project-polygon-${id}-progress-0`,
        12,
      );

      setHasZoomed(progressDidZoom);
    } else if (planningFeatureAdded) {
      const planDidZoom = zoomOnFeature(
        map,
        `project-polygon-${id}-plan-0`,
        12,
      );
      setHasZoomed(planDidZoom);
    }
  }, [
    hasZoomed,
    id,
    map,
    progressFeatureAdded,
    planningFeatureAdded,
    progressLoading,
    planningLayer,
    progressLayer,
  ]);

  // suunnitelman piirto

  React.useEffect(() => {
    const planSource = planningLayer?.getSource();

    if (
      !map ||
      !planningLayer ||
      id === undefined ||
      !options ||
      !planGeoJson
    ) {
      planSource?.clear();
      return;
    }
    const {
      showPlanning,

      vessels: activeVessels,
      areaOptions,
    } = options;

    planSource?.clear();
    if (!showPlanning) return;
    const filteredGeoJsonFeatures = planGeoJson.features.filter((f) => {
      const area = f.properties.SubArea;
      if (!area) return true;
      return filterByCheckBox({
        activeAreas: areaOptions,
        activeVessels,
        area,
      });
    });

    const filteredGeoJson = {
      ...planGeoJson,
      features: filteredGeoJsonFeatures,
    };

    const features = new GeoJSON().readFeatures(filteredGeoJson);
    setFilteredPlanFeatures(features);
    features.forEach((f, i) => {
      const mapProperties = projectShapePlanToMapItem(
        f.getProperties() as GeojsonAttributes,
        t("projects:plan"),
      );
      f.setProperties({ ...mapProperties });
      f.setId(`project-polygon-${id}-plan-${i}`);
    });

    planSource?.addFeatures(features);
    if (features.length > 0) setPlanningFeatureAdded(true);
  }, [id, map, options, planGeoJson, planningLayer, t]);

  // etenemän piirto

  React.useEffect(() => {
    if (
      !map ||
      !progressLayer ||
      id === undefined ||
      !options ||
      !mapItemColors ||
      !progressGeoJson
    )
      return;
    const source = progressLayer.getSource();
    progressLayer.setMinZoom(10);

    source.clear();
    const secondaryColor = theme.palette.primary.main;

    const {
      vessels: activeVessels,
      areaOptions: activeAreas,
      colorBy,
    } = options;

    // filter progress based on settings
    const filteredGeojsonGeometry = progressGeoJson.features.filter(
      (feature, index) => {
        const vesselFeature = feature.properties.Vessel;

        return (
          sliderValue >= index &&
          filterByCheckBox({
            activeAreas,
            activeVessels,
            area: feature.properties.SubArea,
            vessel: vesselFeature,
          })
        );
      },
    );

    const filteredGeoJson = {
      ...progressGeoJson,
      features: filteredGeojsonGeometry,
    };
    const geoJsonObject = new GeoJSON();

    const features = geoJsonObject.readFeatures(filteredGeoJson);
    // save to state so useCellnamePolygon can use these

    features.forEach((feature, featureIndex) => {
      const featureProperties = feature.getProperties() as GeojsonAttributes;
      const colorProp = featureProperties?.[colorBy];
      const mapProperties = projectShapeProgressToMapItem(
        featureProperties,
        t("projects:shapeProgress"),
      );
      feature.setProperties({ ...mapProperties });
      const fillColor = alpha(
        colorBy && colorProp
          ? mapItemColors?.[colorBy]?.[colorProp].hex() ?? secondaryColor
          : secondaryColor,
        progressOpacity,
      );
      const pattern = createCanvasPattern(fillColor);
      feature.setStyle(
        new Style({
          stroke: new Stroke({
            width: 2,
          }),
          fill: new Fill({ color: pattern ?? undefined }),
        }),
      );
      feature.setId(`project-polygon-${id}-progress-${featureIndex}`);
      progressLayer.getSource().addFeature(feature);
    });
    if (features.length > 0) setProgressFeatureAdded(true);
  }, [
    id,
    map,
    mapItemColors,
    options,
    progressGeoJson,
    progressLayer,
    progressOpacity,
    sliderValue,
    t,
    theme.palette.primary.main,
  ]);

  return { planningFeatureAdded };
}

type FilterByCheckBox = {
  activeVessels: string[];
  vessel?: string;
  activeAreas: string[];
  area: string;
};

function filterByCheckBox({
  activeVessels,
  vessel,
  activeAreas,
  area,
}: FilterByCheckBox) {
  if (!vessel) return activeAreas.includes(area);
  return activeVessels.includes(vessel) && activeAreas.includes(area);
}
